import React, { useState, useRef, useCallback } from 'react';
import { api } from '../api/client';

export default function StrainImageManager({ strain, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [testDesc, setTestDesc] = useState(strain.test_description || '');
  const [savingDesc, setSavingDesc] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const images = strain.images || [];

  const doUpload = useCallback((files) => {
    if (!files || files.length === 0) return;

    const totalSize = Array.from(files).reduce((s, f) => s + f.size, 0);
    const sizeMB = (totalSize / 1024 / 1024).toFixed(1);

    setUploading(true);
    setProgress(0);
    setStatus(`Uploading ${files.length} photo${files.length > 1 ? 's' : ''} (${sizeMB} MB)...`);
    setError('');

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/admin/strains/${strain.id}/images`);
    xhr.withCredentials = true;
    xhr.timeout = 120000;

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setProgress(pct);
        if (pct >= 100) setStatus('Processing images...');
      }
    };

    xhr.onload = () => {
      if (inputRef.current) inputRef.current.value = '';
      if (xhr.status >= 200 && xhr.status < 300) {
        setStatus('Done!');
        setProgress(100);
        onUpdate();
        setTimeout(() => { setStatus(''); setProgress(0); }, 2000);
      } else {
        let msg = `Upload failed (${xhr.status})`;
        try { msg = JSON.parse(xhr.responseText)?.error || msg; } catch {}
        setError(msg);
        setStatus('');
      }
      setUploading(false);
    };

    xhr.onerror = () => {
      if (inputRef.current) inputRef.current.value = '';
      setError('Network error during upload');
      setStatus('');
      setUploading(false);
    };

    xhr.ontimeout = () => {
      if (inputRef.current) inputRef.current.value = '';
      setError('Upload timed out');
      setStatus('');
      setUploading(false);
    };

    xhr.send(formData);
  }, [strain.id, onUpdate]);

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      doUpload(files);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/admin/strains/${strain.id}/images/${imageId}`);
      onUpdate();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReorder = async (index, direction) => {
    const newImages = [...images];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newImages.length) return;
    [newImages[index], newImages[swapIndex]] = [newImages[swapIndex], newImages[index]];
    const imageIds = newImages.map(img => img.id);
    try {
      await api.put(`/admin/strains/${strain.id}/images/reorder`, { imageIds });
      onUpdate();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveDesc = async () => {
    setSavingDesc(true);
    setError('');
    try {
      await api.put(`/admin/strains/${strain.id}/test-description`, { test_description: testDesc });
      onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDesc(false);
    }
  };

  return (
    <div style={{
      marginTop: 12, padding: 12, background: 'var(--bg-secondary)',
      borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)'
    }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)' }}>
        Photos & Test Description: {strain.name}
      </h4>

      {error && <div className="auth-error" style={{ marginBottom: 8, fontSize: 12 }}>{error}</div>}

      {/* Image upload — label wraps input for iOS Safari compatibility */}
      <div style={{ marginBottom: 12 }}>
        <label
          style={{
            display: 'inline-block', padding: '6px 14px', fontSize: 12,
            background: 'var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)',
            cursor: uploading ? 'wait' : 'pointer',
            opacity: uploading ? 0.6 : 1,
          }}
        >
          {uploading ? 'Uploading...' : '+ Upload Photos'}
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileChange}
            style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', opacity: 0 }}
          />
        </label>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
          JPG, PNG, WebP (max 10MB each, up to 5)
        </span>

        {/* Progress bar */}
        {(uploading || status) && (
          <div style={{ marginTop: 8 }}>
            <div style={{
              height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 3,
                background: progress >= 100 ? 'var(--success)' : 'var(--accent)',
                width: `${progress}%`,
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {status} {uploading && progress < 100 ? `${progress}%` : ''}
            </div>
          </div>
        )}
      </div>

      {/* Thumbnail grid */}
      {images.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
          gap: 8, marginBottom: 12
        }}>
          {images.map((img, i) => (
            <div key={img.id} style={{
              position: 'relative', borderRadius: 'var(--radius-sm)',
              overflow: 'hidden', border: '1px solid var(--border)', background: '#000'
            }}>
              <img
                src={img.url}
                alt=""
                style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
              />
              <div style={{
                position: 'absolute', top: 0, right: 0, display: 'flex', gap: 2, padding: 2
              }}>
                {i > 0 && (
                  <button
                    onClick={() => handleReorder(i, -1)}
                    style={{
                      background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none',
                      borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '2px 5px', lineHeight: 1,
                    }}
                    title="Move left"
                  >
                    &#8592;
                  </button>
                )}
                {i < images.length - 1 && (
                  <button
                    onClick={() => handleReorder(i, 1)}
                    style={{
                      background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none',
                      borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '2px 5px', lineHeight: 1,
                    }}
                    title="Move right"
                  >
                    &#8594;
                  </button>
                )}
                <button
                  onClick={() => handleDelete(img.id)}
                  style={{
                    background: 'rgba(220,38,38,0.85)', color: '#fff', border: 'none',
                    borderRadius: 3, cursor: 'pointer', fontSize: 11, padding: '2px 5px', lineHeight: 1,
                  }}
                  title="Delete"
                >
                  &times;
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Test description */}
      <div>
        <label style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
          Test Description
        </label>
        <textarea
          value={testDesc}
          onChange={e => setTestDesc(e.target.value)}
          placeholder="Lab results, THC %, terpene profile..."
          rows={3}
          style={{
            width: '100%', fontSize: 12, padding: 8, borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)', background: 'var(--bg-primary)',
            color: 'var(--text-primary)', resize: 'vertical', boxSizing: 'border-box',
          }}
        />
        <button
          className="btn-primary btn-small"
          onClick={handleSaveDesc}
          disabled={savingDesc}
          style={{ marginTop: 6, fontSize: 11 }}
        >
          {savingDesc ? 'Saving...' : 'Save Description'}
        </button>
      </div>
    </div>
  );
}
